import { SetOptional } from 'type-fest'

import api from '@libs/api'

import FileUploadService from '@services/file-upload'

import DateUtils from '@utils/dates'
import { handleServiceError } from '@utils/errors'
import {
  IApiData,
  IEntityValidation,
  IFilter,
  IPublicWhitePaper,
  IPublicWhitePaperWithNeighborSlugs,
  IQueryFilters,
  PublicWhitePaperStatus,
  PublicWhitePaperType,
} from '@utils/types'
import UrlUtils from '@utils/url'

const getUpdatedQueryFilter = ({
  queryFilters,
  additionalFilters,
}: {
  queryFilters: SetOptional<IQueryFilters, 'page' | 'limit'> | undefined
  additionalFilters?: IFilter[]
}) => {
  const currentFilters = queryFilters?.filters

  return {
    ...queryFilters,
    filters: [...(currentFilters ?? []), ...(additionalFilters ?? [])],
  }
}

const findAllAsVisitor = async ({
  whitePaperType,
  queryFilters,
}: {
  whitePaperType?: PublicWhitePaperType
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
}): Promise<IApiData<IPublicWhitePaper[]>> => {
  const whitePaperTypeFilter = whitePaperType
    ? [{ field: 'type', value: whitePaperType }]
    : []

  const queryString = UrlUtils.buildQueryString(
    getUpdatedQueryFilter({
      queryFilters,
      additionalFilters: whitePaperTypeFilter,
    })
  )
  try {
    const response = (await api.get({
      path: '/public-whitepapers/public',
      queryString,
      authenticate: false,
    })) as IApiData<IPublicWhitePaper[]>

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllAsAdmin = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IPublicWhitePaper[]>> => {
  const queryString = UrlUtils.buildQueryString(queryFilters)

  try {
    const response = await api.get({
      path: '/public-whitepapers',
      queryString,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findById = async (id: string): Promise<IPublicWhitePaper> => {
  try {
    const response = await api.get({
      path: `/public-whitepapers/${id}`,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findBySlugAsVisitor = async (
  slug: string
): Promise<IPublicWhitePaperWithNeighborSlugs> => {
  try {
    const response = await api.get({
      path: `/public-whitepapers/${slug}/public`,
      authenticate: false,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const create = async ({
  publicWhitePaper,
}: {
  publicWhitePaper: Partial<IPublicWhitePaper>
}) => {
  const {
    title,
    type,
    image,
    status,
    publishedDate,
    description,
    paper,
    paperFileName,
  } = publicWhitePaper

  let savedFileName: string | null | undefined

  try {
    if (!!paper) {
      savedFileName = await FileUploadService.uploadFile({
        apiPath: '/public-whitepapers',
        data: paper as File,
      })
    } else {
      savedFileName = paper
    }

    const response = (await api.post({
      path: '/public-whitepapers',
      data: api.buildFormData({
        title,
        type,
        image,
        paper: savedFileName,
        paperFileName: savedFileName ?? paperFileName,
        description,
        status,
        publishedDate,
      }),
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPublicWhitePaper

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const update = async ({
  publicWhitePaper,
  deleteImage,
  deletePaper,
}: {
  publicWhitePaper: Pick<IPublicWhitePaper, 'id'> & Partial<IPublicWhitePaper>
  deleteImage?: boolean
  deletePaper?: boolean
}) => {
  const {
    id,
    title,
    type,
    image,
    publishedDate,
    description,
    status,
    paper,
    paperFileName,
  } = publicWhitePaper

  let savedFileName: string | null | undefined

  try {
    if (!!paper) {
      savedFileName = await FileUploadService.uploadFile({
        apiPath: '/public-whitepapers',
        data: paper as File,
      })
    } else {
      savedFileName = paper
    }

    const response = (await api.put({
      path: `/public-whitepapers/${id}`,
      data: api.buildFormData({
        title,
        type,
        image,
        paper: savedFileName,
        paperFileName: savedFileName ?? paperFileName,
        description,
        status,
        publishedDate,
        deleteImage,
        deletePaper,
      }),
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPublicWhitePaper

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const validateWhitePaper = async ({
  id,
}: Pick<IPublicWhitePaper, 'id'>): Promise<IEntityValidation> => {
  try {
    const response = (await api.get({
      path: `/public-whitepapers/${id}/validate`,
    })) as IPublicWhitePaper

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const updateStatus = async ({
  id,
  status: newStatus,
}: Pick<IPublicWhitePaper, 'id' | 'status'>): Promise<IPublicWhitePaper> => {
  const form = api.buildFormData({ status: newStatus })
  try {
    const response = (await api.put({
      path: `/public-whitepapers/${id}/update-status`,
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPublicWhitePaper

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const publish = ({ id }: Pick<IPublicWhitePaper, 'id'>) =>
  updateStatus({ id, status: PublicWhitePaperStatus.PUBLISHED })

const unpublish = ({ id }: Pick<IPublicWhitePaper, 'id'>) =>
  updateStatus({ id, status: PublicWhitePaperStatus.DRAFT })

const remove = async ({ id }: Pick<IPublicWhitePaper, 'id'>) => {
  try {
    const response = (await api.remove({
      path: `/public-whitepapers/${id}`,
    })) as IPublicWhitePaper

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const getSlug = async ({ title }: { title: string }) => {
  const encodedTitle = encodeURIComponent(title)

  try {
    const response = (await api.get({
      path: `/public-whitepapers/${encodedTitle}/slug`,
    })) as string

    return response
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export default {
  create,
  update,
  findAllAsAdmin,
  findAllAsVisitor,
  findById,
  findBySlugAsVisitor,
  validateWhitePaper,
  publish,
  unpublish,
  remove,
  getSlug,
}
